import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Overlay from 'components/Basic/Overlay/Overlay';
import { LoginUnderMenuWrapper } from 'components/Blocks/Popup/Login/LoginUnderMenuWrapper';
import UserMenu from 'components/Layout/Header/HeaderBottom/UserMenu/UserMenu';
import { mobileFirstSizes } from 'components/Theme/mediaQueries';
import { MENU_HOVER_DELAY } from 'config/constants';
import { useBlogUrl } from 'connectors/blogCategory/BlogCategory';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isWebDomain } from 'helpers/isDomainLocale';
import useTimeout from 'hooks/helpers/UseTimeout';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { BlogIcon, MenuHeartIcon, MenuUserIcon } from 'public/svg/IconsSvg';
import { memo, useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import twMerge from 'utils/twMerge';

const TEST_IDENTIFIER = 'layout-header-menuiconic';

const isBrowserPasswordManagerHovered = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    return e.relatedTarget === window;
};

export const MenuIconic = memo(() => {
    const { t } = useTranslation();
    const currentDomainConfig = useDomainConfig();
    const isB2b = isB2bDomain(currentDomainConfig);
    const isWeb = isWebDomain(currentDomainConfig);

    const router = useRouter();
    const { isUserLoggedIn } = useCurrentUserData();
    const [isUserPopupOpened, setIsUserPopupOpened] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const [isUserMenuHovered, setIsUserMenuHovered] = useState(false);
    const [isUserMenuFocused, setIsUserMenuFocused] = useState(false);
    const userMenuLinkRef = useRef<HTMLDivElement>(null);
    const { wishlist } = useWishlist();
    const [wishlistUrl] = getInternationalizedStaticUrls(['/wishlist'], currentDomainConfig.url);
    const blogUrl = useBlogUrl({ pause: !isB2b });

    useEffect(() => {
        if (isUserLoggedIn) {
            setIsUserPopupOpened(false);
        }
    }, [isUserLoggedIn]);

    useTimeout(
        () => setIsUserPopupOpened(true),
        !isUserPopupOpened && isUserMenuHovered && !isMobileView ? MENU_HOVER_DELAY : null,
    );

    useTimeout(
        () => setIsUserPopupOpened(false),
        isUserPopupOpened && !isUserMenuHovered && !isUserMenuFocused && !isMobileView ? MENU_HOVER_DELAY : null,
    );

    useEffect(() => {
        const closeLoginPopup = () => {
            setIsUserMenuHovered(false);
            setIsUserMenuFocused(false);
            setIsUserPopupOpened(false);
        };

        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isUserPopupOpened) {
                closeLoginPopup();
            }
        };

        router.events.on('routeChangeStart', closeLoginPopup);
        document.addEventListener('keydown', handleEscapeKey);

        const handler = () => setIsMobileView(window.innerWidth < mobileFirstSizes.vl);
        handler();
        window.addEventListener('resize', handler, { passive: true });

        return () => {
            window.removeEventListener('resize', handler);
            document.removeEventListener('keydown', handleEscapeKey);
            router.events.off('routeChangeStart', closeLoginPopup);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserPopupOpened]);

    return (
        <>
            <ul className="flex h-full vl:not-last:mr-2.5" data-testid={TEST_IDENTIFIER}>
                {!isB2b && (
                    <li className="relative space-x-1 self-center notVl:h-[45px] notVl:w-[45px] notVl:items-center notVl:justify-center notVl:space-x-0 notVl:bg-primary notVl:text-white">
                        <ExtendedNextLink
                            className="flex h-full w-full cursor-pointer items-center justify-center font-bold text-currentColor no-underline transition-all vl:h-[48px] vl:rounded-md vl:px-3 vl:hover:bg-white vl:hover:no-underline notVl:hover:text-white"
                            href={wishlistUrl}
                            title={t('Open wishlist')}
                            type="wishlist"
                        >
                            <>
                                <div className="relative">
                                    <MenuHeartIcon className="mr-2.5 h-6 w-6 stroke-white text-currentColor vl:stroke-black notVl:mr-0" />
                                    {wishlist?.products.length && (
                                        <span
                                            data-testid={TEST_IDENTIFIER + '-wishlist-itemcount'}
                                            className={twMerge(
                                                'absolute -top-2 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-red text-[13px] font-bold text-white notVl:-right-2',
                                                wishlist.products.length > 99 && 'h-6 w-6',
                                            )}
                                        >
                                            {wishlist.products.length}
                                        </span>
                                    )}
                                </div>
                                <span className="notVl:hidden">{t('Favorite')}</span>
                            </>
                        </ExtendedNextLink>
                    </li>
                )}

                {isB2b && blogUrl && (
                    <li className="relative space-x-1 self-center notVl:h-[45px] notVl:w-[45px] notVl:items-center notVl:justify-center notVl:space-x-0 notVl:bg-primary notVl:text-white">
                        <ExtendedNextLink
                            className="flex h-full w-full cursor-pointer items-center justify-center font-bold text-currentColor no-underline transition-all vl:h-[48px] vl:rounded-md vl:px-3 vl:hover:bg-white vl:hover:no-underline notVl:hover:text-white"
                            href={blogUrl}
                            title={t('B2B - Open blog')}
                            type="blogCategory"
                        >
                            <>
                                <div className="relative">
                                    <BlogIcon className="mr-2.5 h-6 w-6 stroke-white text-currentColor vl:stroke-black notVl:mr-0" />
                                </div>
                                <span className="notVl:hidden">{t('B2B - Blog')}</span>
                            </>
                        </ExtendedNextLink>
                    </li>
                )}

                {!isWeb && (
                    <li
                        data-testid={TEST_IDENTIFIER + '-2'}
                        className={twJoin(
                            'relative space-x-1 self-center notVl:h-[45px] notVl:w-[45px] notVl:items-center notVl:justify-center notVl:space-x-0',
                            isUserPopupOpened && 'z-menu notVl:bg-white notVl:text-primary',
                            !isUserPopupOpened && 'notVl:bg-primary notVl:text-white',
                        )}
                    >
                        <div
                            ref={userMenuLinkRef}
                            className={twJoin(
                                'flex h-full w-full cursor-pointer items-center justify-center font-bold no-underline transition-all vl:h-[48px] vl:rounded-md vl:px-3 vl:hover:bg-white vl:hover:no-underline',
                                isUserPopupOpened && 'bg-white text-primary',
                            )}
                            onFocus={() => setIsUserMenuFocused(true)}
                            onMouseEnter={!isMobileView ? () => setIsUserMenuHovered(true) : undefined}
                            onBlur={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    setIsUserMenuFocused(false);
                                }
                            }}
                            onMouseLeave={
                                !isMobileView
                                    ? (e) => !isBrowserPasswordManagerHovered(e) && setIsUserMenuHovered(false)
                                    : undefined
                            }
                        >
                            <button
                                aria-label={t('Open user menu')}
                                className="text-currentColor"
                                tabIndex={0}
                                onClick={() => {
                                    setIsUserMenuHovered(true);
                                    setIsUserPopupOpened((prev) => !prev);
                                }}
                            >
                                <MenuUserIcon
                                    className={twMerge(
                                        'mr-2.5 h-6 w-6 stroke-white text-currentColor vl:stroke-black notVl:mr-0',
                                        isUserPopupOpened && 'stroke-primary',
                                    )}
                                />
                            </button>
                            <span className="notVl:hidden">{isUserLoggedIn ? t('My account') : t('Log in')}</span>
                            {isUserPopupOpened && isUserLoggedIn && (
                                <UserMenu
                                    isActive={isUserPopupOpened}
                                    isMobileView={isMobileView}
                                    userMenuLinkRef={userMenuLinkRef}
                                />
                            )}
                            {isUserPopupOpened && !isUserLoggedIn && (
                                <LoginUnderMenuWrapper isMobileView={isMobileView} userMenuLinkRef={userMenuLinkRef} />
                            )}
                        </div>
                    </li>
                )}
            </ul>
            {!isWeb && (
                <Overlay
                    className="z-overlay"
                    isActive={isUserPopupOpened}
                    withWrapper={false}
                    onClick={() => {
                        setIsUserPopupOpened(false);
                        setIsUserMenuHovered(false);
                        setIsUserMenuFocused(false);
                    }}
                    onMouseEnter={() => {
                        setIsUserMenuHovered(false);
                    }}
                />
            )}
        </>
    );
});

MenuIconic.displayName = 'MenuIconic';
